import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Brain, Pencil, Trash2, Check, X } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow } from "@/components/fionova/Primitives";
import { Switch } from "@/components/ui/switch";
import { getCompanion, editNote, deleteNote, setCompanionSettings, clearEllieHistory } from "@/services/unsaidService";
import { ELLIE_CHAT_PATH } from "@/components/companion/features";
import { authHeaders } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  { kind: "fact", title: "Things you've told me" },
  { kind: "observation", title: "Ellie's tentative observations", hint: "Possibilities, not conclusions. Correct anything that doesn't fit." },
  { kind: "preference", title: "Your preferences" },
];

const NoteRow = ({ note, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  return (
    <li data-testid={`memory-note-${note.id}`} className="rounded-2xl bg-card p-4 ring-1 ring-border">
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea data-testid={`memory-note-input-${note.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2}
            className="w-full resize-none rounded-xl bg-background/60 px-3 py-2 text-sm text-foreground ring-1 ring-border focus:outline-none focus:ring-2 focus:ring-primary" />
          <div className="flex justify-end gap-2">
            <button data-testid={`memory-note-cancel-${note.id}`} onClick={() => { setText(note.text); setEditing(false); }} className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /> Cancel</button>
            <button data-testid={`memory-note-save-${note.id}`} disabled={!text.trim()} onClick={async () => { await onSave(note, text.trim()); setEditing(false); }} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"><Check className="h-3.5 w-3.5" /> Save</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <p className="flex-1 text-sm leading-relaxed text-foreground/90">{note.text}</p>
          <button data-testid={`memory-note-edit-${note.id}`} onClick={() => setEditing(true)} aria-label="Edit memory" className="rounded-full p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`memory-note-delete-${note.id}`} onClick={() => onDelete(note)} aria-label="Delete memory" className="rounded-full p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </li>
  );
};

const Toggle = ({ id, label, desc, checked, onChange }) => (
  <label htmlFor={id} className="flex items-start gap-4 rounded-2xl bg-card p-4 ring-1 ring-border">
    <span className="flex-1">
      <span className="block text-sm font-medium text-foreground">{label}</span>
      <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{desc}</span>
    </span>
    <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onChange} />
  </label>
);

// Optional emails: off unless the person opted in. Security, password and purchase emails are unaffected.
const EmailPrefToggle = () => {
  const [on, setOn] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/me/email-preferences`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setOn(!!d.marketing)).catch(() => {});
  }, []);
  const change = async (v) => {
    setOn(v);
    const r = await fetch(`${API}/api/me/email-preferences`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ marketing: v }) }).catch(() => null);
    if (r?.ok) setOn(!!(await r.json()).marketing);
  };
  if (on === null) return null;
  return <Toggle id="memory-email-optin" label="Occasional emails from FioNova" desc="Helpful wellness insights, product updates, and encouragement. Off unless you opt in. Account security, password and purchase emails are always sent." checked={on} onChange={change} />;
};

export default function MemoryPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [cleared, setCleared] = useState(false);
  useEffect(() => { getCompanion().then(setData).catch(() => setError(true)); }, []);

  const save = async (note, text) => {
    await editNote(note.id, text);
    setData((d) => ({ ...d, notes: d.notes.map((n) => (n.id === note.id ? { ...n, text, locked: true } : n)) }));
  };
  const remove = async (note) => {
    await deleteNote(note.id);
    setData((d) => ({ ...d, notes: d.notes.filter((n) => n.id !== note.id) }));
  };
  const toggle = async (patch) => {
    setData((d) => ({ ...d, settings: { ...d.settings, ...patch } }));
    const settings = await setCompanionSettings(patch).catch(() => null);
    if (settings) setData((d) => ({ ...d, settings }));
  };
  const clearChat = async () => {
    if (!window.confirm("Clear your whole conversation with Ellie? Your memories below stay unless you delete them.")) return;
    await clearEllieHistory(); setCleared(true);
  };

  return (
    <div data-testid="page-memory" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex min-h-screen w-full max-w-[600px] flex-col px-6 pb-28 pt-8 sm:px-8">
        <div className="fio-appbar sticky top-3 z-20 flex items-center justify-between rounded-full py-1.5 pl-4 pr-1.5" data-testid="app-header">
          <button data-testid="memory-back" onClick={() => navigate("/app")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Home</button>
          <Logo size={36} />
        </div>
        <div className="mt-8">
          <div className="flex items-center gap-2 text-primary"><Brain className="h-4 w-4" /><Eyebrow>Your memory</Eyebrow></div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">What Ellie remembers</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Only you and Ellie see this. Edit anything that isn't right — your edits are kept as written. Deleted memories stay forgotten unless you share them again.</p>
        </div>
        {error && <p data-testid="memory-error" className="mt-6 text-sm text-muted-foreground">Couldn't load your memory just now. Please try again.</p>}
        {data && (
          <>
            <p className="eyebrow mt-8 text-[11px] text-muted-foreground" data-testid="privacy-personalization">Privacy &amp; Personalization</p>
            <div className="mt-3 space-y-3">
              <Toggle id="memory-share-journal" label="Let Ellie read my journal" desc="Off by default. When off, Ellie only sees entries you share one by one from your Journal." checked={data.settings.share_journal} onChange={(v) => toggle({ share_journal: v })} />
              <Toggle id="memory-share-checkins" label="Share my daily check-ins with Ellie" desc="Allow Ellie to use your daily check-ins to better understand how you're feeling, recognize patterns over time, and personalize your ongoing conversations. You can turn this on or off anytime. (Off unless you turn it on. Covers your latest Emotional X-Ray, Daily Battle Plan, Future Self and Purpose results.)" checked={data.settings.share_checkins} onChange={(v) => toggle({ share_checkins: v })} />
              <EmailPrefToggle />
            </div>
            {GROUPS.map(({ kind, title, hint }) => {
              const notes = data.notes.filter((n) => n.kind === kind);
              return (
                <section key={kind} className="mt-8" data-testid={`memory-group-${kind}`}>
                  <p className="eyebrow text-[11px] text-muted-foreground">{title}</p>
                  {hint && <p className="mt-1 text-xs text-muted-foreground/80">{hint}</p>}
                  {notes.length === 0 ? <p className="mt-3 text-sm text-muted-foreground/80">Nothing here yet.</p> : (
                    <ul className="mt-3 space-y-2.5">{notes.map((n) => <NoteRow key={n.id} note={n} onSave={save} onDelete={remove} />)}</ul>
                  )}
                </section>
              );
            })}
            <div className="mt-10 rounded-3xl border border-border p-5">
              <p className="text-sm font-medium text-foreground">Conversation history</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{cleared ? "Your conversation has been cleared." : `${data.conversation_count} saved messages with Ellie.`}</p>
              {!cleared && <button data-testid="memory-clear-chat" onClick={clearChat} className="mt-3 rounded-full border border-destructive/40 px-4 py-2 text-xs font-medium text-destructive hover:bg-destructive/10">Clear conversation</button>}
            </div>
            <button data-testid="memory-to-ellie" onClick={() => navigate(ELLIE_CHAT_PATH)} className="mt-6 text-sm font-medium text-primary hover:underline">Talk to Ellie</button>
          </>
        )}
      </div>
    </div>
  );
}
